import { Head, Link } from '@inertiajs/react'
import MainLayout from '../components/layout/MainLayout'
import Button from '../components/ui/Button'

/**
 * About page - À propos de SmartImmo
 */
export default function About() {
  const values = [
    {
      title: 'Innovation',
      description: 'Nous utilisons l\'IA pour révolutionner la génération de leads immobiliers',
      icon: '💡'
    },
    {
      title: 'Accessibilité',
      description: 'Des solutions simples et abordables pour toutes les agences',
      icon: '🤝'
    },
    {
      title: 'Performance',
      description: 'Des leads qualifiés et un ROI mesurable dès le premier jour',
      icon: '📈'
    }
  ]

  return (
    <MainLayout>
      <Head title="À propos - SmartImmo">
        <meta name="description" content="Découvrez SmartImmo, la plateforme qui démocratise l'accès aux leads immobiliers qualifiés grâce à l'Intelligence Artificielle." />
        <meta property="og:title" content="À propos - SmartImmo" />
        <meta property="og:description" content="Notre mission : démocratiser l'accès aux leads immobiliers qualifiés pour toutes les agences." />
      </Head>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-20">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Notre mission
          </h1>
          <p className="text-xl text-gray-600">
            Démocratiser l'accès aux leads immobiliers qualifiés
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="prose prose-lg mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">L'histoire de SmartImmo</h2>

            <p className="text-gray-600 mb-4">
              SmartImmo est née d'un constat simple : les agences immobilières dépensent des fortunes en publicité
              et en leads de mauvaise qualité, alors que leurs meilleures opportunités passent tous les jours devant
              leur vitrine.
            </p>

            <p className="text-gray-600 mb-4">
              Nous avons créé une solution simple : un QR code intelligent qui transforme chaque visiteur en agence
              en lead qualifié, grâce à notre agent IA conversationnel. Plus besoin de mobiliser un agent commercial,
              plus besoin de formulaires compliqués. L'IA s'occupe de tout.
            </p>

            <p className="text-gray-600 mb-4">
              Aujourd'hui, plus de 15 000 agences nous font confiance pour générer leurs leads au quotidien.
              Notre technologie a permis de créer plus de 100 000 connexions entre agences et clients potentiels.
            </p>
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Un partenariat solide</h2>
            <p className="text-lg text-gray-600">
              Propulsé par Vitrine Immo, réseau de 15 000 agences immobilières
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <p className="text-gray-600 mb-4">
              SmartImmo s'appuie sur le réseau Vitrine Immo, qui rassemble plus de 15 000 agences immobilières
              à travers la France. Ce partenariat nous permet d'offrir une solution éprouvée, adaptée aux besoins
              réels des professionnels de l'immobilier.
            </p>
            <p className="text-gray-600">
              Grâce à cette collaboration, nous comprenons les défis quotidiens des agences et développons
              des fonctionnalités qui répondent à leurs attentes concrètes.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Nos valeurs</h2>
            <p className="text-lg text-gray-600">
              Ce qui guide notre travail au quotidien
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6">
                <div className="text-5xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Notre technologie</h2>
            <p className="text-lg text-gray-600">
              Une IA conversationnelle spécialement entraînée pour l'immobilier
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">🎯 Qualification intelligente</h3>
                <p className="text-gray-600 text-sm">
                  Notre IA pose les bonnes questions au bon moment pour qualifier chaque lead avec précision
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">🧠 Apprentissage continu</h3>
                <p className="text-gray-600 text-sm">
                  Le système s'améliore continuellement grâce aux interactions avec les visiteurs
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">⚡ Instantané</h3>
                <p className="text-gray-600 text-sm">
                  Les leads arrivent en temps réel dans votre dashboard, prêts à être traités
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">🔒 Sécurisé</h3>
                <p className="text-gray-600 text-sm">
                  Toutes les données sont cryptées et conformes au RGPD
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Rejoignez l'aventure
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Faites partie des agences qui transforment leurs visiteurs en clients
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/sign-up">
              <Button variant="secondary" size="lg">
                Créer mon compte
              </Button>
            </Link>
            <Link href="/contact">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-600">
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </MainLayout>
  )
}
